import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { EXTERNAL_NAVIGATION_EVENT } from "../../../utils/externalNavigation";
import "./ExternalLinkConfirmation.css";

const isExternalWebsite = (url) => {
  if (!url || !["http:", "https:"].includes(url.protocol)) {
    return false;
  }

  const normalizeHostname = (hostname) => hostname.replace(/^www\./i, "");

  return normalizeHostname(url.hostname) !== normalizeHostname(window.location.hostname);
};

const getExternalUrl = (value) => {
  try {
    const url = new URL(value, window.location.href);
    return isExternalWebsite(url) ? url.href : null;
  } catch {
    return null;
  }
};

const ExternalLinkConfirmation = () => {
  const [pendingUrl, setPendingUrl] = useState(null);
  const okayButtonRef = useRef(null);
  const cancelButtonRef = useRef(null);

  useEffect(() => {
    const handleDocumentClick = (event) => {
      if (event.defaultPrevented || event.button !== 0) {
        return;
      }

      const anchor = event.target.closest?.("a[href]");

      if (anchor?.dataset.skipExternalConfirmation === "true") {
        return;
      }

      const externalUrl = getExternalUrl(anchor?.getAttribute("href"));

      if (!externalUrl) {
        return;
      }

      event.preventDefault();
      setPendingUrl(externalUrl);
    };

    const handleExternalNavigationRequest = (event) => {
      const externalUrl = getExternalUrl(event.detail?.url);

      if (externalUrl) {
        setPendingUrl(externalUrl);
      }
    };

    document.addEventListener("click", handleDocumentClick);
    window.addEventListener(
      EXTERNAL_NAVIGATION_EVENT,
      handleExternalNavigationRequest,
    );

    return () => {
      document.removeEventListener("click", handleDocumentClick);
      window.removeEventListener(
        EXTERNAL_NAVIGATION_EVENT,
        handleExternalNavigationRequest,
      );
    };
  }, []);

  useEffect(() => {
    if (!pendingUrl) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    const previousFocus = document.activeElement;
    document.body.style.overflow = "hidden";
    okayButtonRef.current?.focus();

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopImmediatePropagation();
        setPendingUrl(null);
      } else if (event.key === "Tab") {
        event.preventDefault();
        const nextButton = event.shiftKey
          ? document.activeElement === cancelButtonRef.current
            ? okayButtonRef.current
            : cancelButtonRef.current
          : document.activeElement === okayButtonRef.current
            ? cancelButtonRef.current
            : okayButtonRef.current;
        nextButton?.focus();
      }
    };

    window.addEventListener("keydown", handleKeyDown, true);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown, true);
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, [pendingUrl]);

  const handleCancel = () => {
    setPendingUrl(null);
  };

  const handleConfirm = () => {
    const url = pendingUrl;
    setPendingUrl(null);
    window.open(url, "_blank", "noopener,noreferrer");
  };

  if (!pendingUrl) {
    return null;
  }

  return createPortal(
    <div
      className="external-link-confirmation__backdrop"
      onClick={handleCancel}
    >
      <section
        className="external-link-confirmation__dialog"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="external-link-confirmation-title"
        aria-describedby="external-link-confirmation-description"
        onClick={(event) => event.stopPropagation()}
      >
        <h2
          className="external-link-confirmation__title"
          id="external-link-confirmation-title"
        >
          Alert
        </h2>
        <p
          className="external-link-confirmation__message"
          id="external-link-confirmation-description"
        >
          You are now exiting our NMDFC website and opening a new website in
          external window
        </p>
        <div className="external-link-confirmation__actions">
          <button
            ref={okayButtonRef}
            className="external-link-confirmation__button external-link-confirmation__button--okay"
            type="button"
            onClick={handleConfirm}
          >
            Okay
          </button>
          <button
            ref={cancelButtonRef}
            className="external-link-confirmation__button external-link-confirmation__button--cancel"
            type="button"
            onClick={handleCancel}
          >
            Cancel
          </button>
        </div>
      </section>
    </div>,
    document.body,
  );
};

export default ExternalLinkConfirmation;
